import React, { useEffect, useRef, useState } from 'react';
import {
  FileUploadInput,
  FlexColumn,
  FormField,
  TextField,
  ToggleButton,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { GcsActivityLogsIntegrationFormValues } from './activityLogsIntegrationUtils';
import FileSizeField from './FileSizeField';

enum GcsAuthMethod {
  SERVICE_ACCOUNT = 'service_account',
  WIF = 'wif',
}

const GcsActivityLogsForm = () => {
  const selectedAuthMethodRef = useRef<HTMLDivElement>(null);
  const methods = useFormContext<GcsActivityLogsIntegrationFormValues>();
  const {
    register,
    control,
    getValues,
    formState: { errors },
  } = methods;

  const initialAuth = getValues('properties.external_account_json')
    ? GcsAuthMethod.WIF
    : GcsAuthMethod.SERVICE_ACCOUNT;
  const [authMethod, setAuthMethod] = useState<GcsAuthMethod>(initialAuth);

  useEffect(() => {
    selectedAuthMethodRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [authMethod]);

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <FormField title="Connection name" errorMessage={errors.name?.message}>
        <TextField
          {...register('name', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="Connection name"
        />
      </FormField>

      <FormField
        title="Bucket name"
        errorMessage={errors.properties?.bucket_name?.message as string}
      >
        <TextField
          {...register('properties.bucket_name', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="e.g. my-elementary-audit-logs"
        />
      </FormField>

      <FormField title="Location" optional>
        <TextField
          {...register('properties.location')}
          variant="outlined"
          size="large"
          placeholder="Optional (e.g. US)"
        />
      </FormField>

      <FormField
        title="Max file size"
        optional
        errorMessage={errors.properties?.max_file_size?.message as string}
      >
        <Controller
          control={control}
          name="properties.max_file_size"
          render={({ field }) => (
            <FileSizeField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </FormField>

      <FormField title="Select authentication method">
        <ToggleButtonGroup
          fullWidth
          value={authMethod}
          onChange={(_, val) => setAuthMethod(val || authMethod)}
          exclusive
        >
          <ToggleButton value={GcsAuthMethod.SERVICE_ACCOUNT} size="large">
            Service account
          </ToggleButton>
          <ToggleButton value={GcsAuthMethod.WIF} size="large">
            Workload Identity Federation
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      <FlexColumn gap="inherit" ref={selectedAuthMethodRef}>
        {authMethod === GcsAuthMethod.SERVICE_ACCOUNT && (
          <FormField
            title="Service account file"
            errorMessage={errors.properties?.keyfile_json?.message as string}
          >
            <Controller
              control={control}
              name="properties.keyfile_json"
              rules={{ required: requiredLabel }}
              render={({ field }) => (
                <FileUploadInput
                  {...field}
                  value={field.value ?? null}
                  placeholder="Upload a service account JSON key file"
                />
              )}
            />
          </FormField>
        )}

        {authMethod === GcsAuthMethod.WIF && (
          <FormField
            title="WIF credential file"
            errorMessage={
              errors.properties?.external_account_json?.message as string
            }
          >
            <Controller
              control={control}
              name="properties.external_account_json"
              rules={{ required: requiredLabel }}
              render={({ field }) => (
                <FileUploadInput
                  {...field}
                  value={field.value ?? null}
                  placeholder="Upload a WIF external account JSON file"
                />
              )}
            />
          </FormField>
        )}
      </FlexColumn>
    </FlexColumn>
  );
};

export default GcsActivityLogsForm;
